import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import {
  RETENTION_DAYS,
  VISIBILITY_GRACE_DAYS,
  deriveLidAantalFromStatus
} from '../config/ledenUploadTemplate';

import { normalizeUitnodigingen } from './conviventieAccess';
import { APP_ERROR_CODES, createAppError } from './appErrors';

const COLLECTION_NAME = 'conviventies';
const GEMEENSCHAPPEN_UPLOADS_SUBCOLLECTION = 'ledenUploadsGemeenschappen';
const GASTENGROEPEN_UPLOADS_SUBCOLLECTION = 'ledenUploadsGastengroepen';

const getUserEmail = () => auth.currentUser?.email || 'unknown';

const normalizeUitgenodigdeGemeenschappen = normalizeUitnodigingen;
const normalizeGuestGroups = normalizeUitnodigingen;

const normalizeDateValue = (value) => {
  if (!value) return '';
  return String(value);
};

const parseYmdDate = (value) => {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toVisibilityCutoffDate = (conviventie) => {
  const endDate = parseYmdDate(conviventie?.eindDatum) || parseYmdDate(conviventie?.datum);
  if (!endDate) return null;
  return addDays(endDate, VISIBILITY_GRACE_DAYS);
};

const computeExpiresAtTimestamp = (eindDatum) => {
  const endDate = parseYmdDate(eindDatum);
  if (!endDate) {
    throw new Error('Eind datum is verplicht en moet formaat YYYY-MM-DD hebben');
  }

  const expiresAtDate = addDays(endDate, RETENTION_DAYS);
  return Timestamp.fromDate(expiresAtDate);
};

const isConviventieVisible = (conviventie, todayDate) => {
  const cutoff = toVisibilityCutoffDate(conviventie);
  if (!cutoff) return false;
  return cutoff >= todayDate;
};

const getAnonymizedLidAantal = (lid) => {
  const explicitAantal = Number(lid?.aantal);
  if (!Number.isNaN(explicitAantal) && explicitAantal > 0) return explicitAantal;
  return deriveLidAantalFromStatus(lid?.status);
};

const mapConfirmedLidToExportRow = ({ gemeenschap, lid, guestGroupName = '' }) => {
  const isAnonymized = !!lid?.anoniem;
  return {
    Bisdom: guestGroupName ? '' : (gemeenschap?.bisdom || ''),
    Parochie: guestGroupName ? '' : (gemeenschap?.parochie || ''),
    GemeenschapNaam: guestGroupName || gemeenschap?.gemeenschapNaam || '',
    Achternaam: isAnonymized ? '' : (lid?.achternaam || ''),
    Voornaam: isAnonymized ? '' : (lid?.voornaam || ''),
    Status: isAnonymized ? '' : (lid?.status || ''),
    Aantal: getAnonymizedLidAantal(lid),
    Geboortedatum: isAnonymized ? '' : (lid?.geboortedatum || ''),
    Email: isAnonymized ? '' : (lid?.email || ''),
    Telefoonnummer: isAnonymized ? '' : (lid?.telefoon || ''),
    Bijzonderheden: isAnonymized ? '' : (lid?.bijzonderheden || ''),
    Hotel: '',
    Kamer: '',
    'Type Kamer': '',
    'Type bed': '',
    Aankomst: isAnonymized ? '' : (lid?.aankomst || ''),
    Vertrek: isAnonymized ? '' : (lid?.vertrek || '')
  };
};

const deleteAllDocsInSubcollection = async (conviventieId, subcollectionName) => {
  const snapshot = await getDocs(collection(db, COLLECTION_NAME, conviventieId, subcollectionName));
  const deletes = [];
  snapshot.forEach((docSnapshot) => {
    deletes.push(deleteDoc(docSnapshot.ref));
  });
  await Promise.all(deletes);
};

/**
 * Drops keys whose value is `undefined`. Firestore rejects such writes with
 * `invalid-argument: Unsupported field value: undefined`, which used to abort the
 * entire confirmation when a legacy/migrated lid carried neither `lidId` nor `id`
 * (Feature 40).
 *
 * @param {Object} record
 * @returns {Object} The record without `undefined` values
 */
const stripUndefinedValues = (record) => Object.fromEntries(
  Object.entries(record).filter(([, value]) => value !== undefined)
);

/**
 * Normalises one confirmed lid into the shape stored on the conviventie document.
 * The result never contains an `undefined` value, so it can always be written.
 *
 * @param {Object} lid
 * @returns {Object} The cleaned confirmed lid
 */
const cleanConfirmedLid = (lid) => {
  const normalizedStatus = lid?.status || '';

  return stripUndefinedValues({
    lidId: lid?.lidId || lid?.id || '',
    voornaam: lid?.voornaam || '',
    achternaam: lid?.achternaam || '',
    status: normalizedStatus,
    aantal: deriveLidAantalFromStatus(normalizedStatus),
    geboortedatum: lid?.geboortedatum || '',
    email: lid?.email || '',
    telefoon: lid?.telefoon || '',
    confirmed: lid?.confirmed !== false,
    bijzonderheden: lid?.bijzonderheden || '',
    aankomst: normalizeDateValue(lid?.aankomst),
    vertrek: normalizeDateValue(lid?.vertrek)
  });
};

/**
 * Sends an ntfy.sh notification after a conviventie confirmation was stored.
 *
 * The notification title is sent as the ntfy `Title` header, which must be
 * ASCII-only; the (UTF-8) message body carries the details.
 *
 * @param {Object} params - Notification payload
 * @param {string} [params.conviventieNaam] - Name of the conviventie
 * @param {string} [params.bisdom] - Diocese of the confirming community (empty for guest groups)
 * @param {string} [params.parochie] - Parish of the confirming community (empty for guest groups)
 * @param {string} [params.gemeenschapNaam] - Confirming community or guest group
 * @param {number} [params.confirmedLedenCount] - Total number of confirmed leden
 * @param {string} [params.confirmedBy] - Email of the user who confirmed the member list
 */
const sendConviventieNotification = async ({
  conviventieNaam,
  bisdom,
  parochie,
  gemeenschapNaam,
  confirmedLedenCount,
  confirmedBy
}) => {
  const ntfyTopic = process.env.REACT_APP_NTFY_CONVIVENTIE_TOPIC;
  if (!ntfyTopic) {
    console.warn('NTFY topic not configured for conviventie notifications, skipping notification');
    return;
  }

  const safeConviventieNaam = conviventieNaam || 'Conviventie';
  const safeBisdom = bisdom || 'Onbekend bisdom';
  const safeParochie = parochie || 'Onbekende parochie';
  const safeGemeenschapNaam = gemeenschapNaam || 'Onbekende gemeenschap';
  const safeConfirmedBy = confirmedBy || 'Onbekend';
  const gemeenschapPad = `${safeBisdom} - ${safeParochie} - ${safeGemeenschapNaam}`;

  try {
    await fetch(`https://ntfy.sh/${ntfyTopic}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        Title: `${safeConviventieNaam} - Bevestiging ontvangen`
      },
      body: `Gemeenschap: ${gemeenschapPad}\nBevestigde leden: ${confirmedLedenCount}\nBevestigd door: ${safeConfirmedBy}`
    });
  } catch (error) {
    console.error('Error sending conviventie notification:', error);
    // Keep confirmation flow resilient even when notification delivery fails.
  }
};

export const getConviventies = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const items = [];
    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (isConviventieVisible(data, todayDate)) {
        items.push({
          id: docSnapshot.id,
          ...data,
          uitgenodigdeGemeenschappen: normalizeUitgenodigdeGemeenschappen(data.uitgenodigdeGemeenschappen),
          invitedGuestGroups: normalizeGuestGroups(data.invitedGuestGroups)
        });
      }
    });

    items.sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));
    return items;
  } catch (error) {
    console.error('Error getting conviventies:', error);
    throw error;
  }
};

/**
 * Returns upcoming conviventies that still reference the given guest group.
 * Used as a safety check before deleting a guest group.
 *
 * @param {string} guestGroupId - The guest group document ID
 * @returns {Promise<Array>} Array of minimal conviventie shapes: { id, naam, datum }
 */
export const getUpcomingConvivenciesReferencingGuestGroup = async (guestGroupId) => {
  try {
    if (!guestGroupId) return [];

    const conviventies = await getConviventies();

    return conviventies
      .filter((conviventie) =>
        (conviventie.invitedGuestGroups || []).some((group) => group?.guestGroupId === guestGroupId)
      )
      .map((conviventie) => ({
        id: conviventie.id,
        naam: conviventie.naam || '',
        datum: conviventie.datum || ''
      }));
  } catch (error) {
    console.error('Error checking guest group references in conviventies:', error);
    throw error;
  }
};

export const addConviventie = async (conviventie) => {
  try {
    if (!conviventie?.eindDatum) {
      throw new Error('Eind datum is verplicht bij het aanmaken van een conviventie');
    }

    const userEmail = getUserEmail();
    const payload = {
      ...conviventie,
      expiresAt: computeExpiresAtTimestamp(conviventie.eindDatum),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userEmail,
      updatedBy: userEmail
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), payload);

    return {
      id: docRef.id,
      ...conviventie,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userEmail,
      updatedBy: userEmail
    };
  } catch (error) {
    console.error('Error adding conviventie:', error);
    throw error;
  }
};

export const updateConviventie = async (id, updates) => {
  try {
    if (!updates?.eindDatum) {
      throw new Error('Eind datum is verplicht bij het bijwerken van een conviventie');
    }

    const userEmail = getUserEmail();
    const payload = {
      ...updates,
      expiresAt: computeExpiresAtTimestamp(updates.eindDatum),
      updatedAt: serverTimestamp(),
      updatedBy: userEmail
    };

    await updateDoc(doc(db, COLLECTION_NAME, id), payload);

    return {
      id,
      ...updates,
      updatedAt: new Date(),
      updatedBy: userEmail
    };
  } catch (error) {
    console.error('Error updating conviventie:', error);
    throw error;
  }
};

export const deleteConviventie = async (id) => {
  try {
    await deleteAllDocsInSubcollection(id, GEMEENSCHAPPEN_UPLOADS_SUBCOLLECTION);
    await deleteAllDocsInSubcollection(id, GASTENGROEPEN_UPLOADS_SUBCOLLECTION);
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting conviventie:', error);
    throw error;
  }
};

export const confirmLeden = async (convivId, gemeenschapId, bevestigdeLeden, targetType = 'gemeenschap') => {
  try {
    const userEmail = getUserEmail();
    const convivRef = doc(db, COLLECTION_NAME, convivId);
    const convivSnapshot = await getDoc(convivRef);

    if (!convivSnapshot.exists()) {
      throw createAppError(APP_ERROR_CODES.CONVIVENTIE_MISSING, 'Conviventie niet gevonden');
    }

    const conviventie = convivSnapshot.data();
    const uitgenodigdeGemeenschappen = normalizeUitgenodigdeGemeenschappen(conviventie.uitgenodigdeGemeenschappen);
    const invitedGuestGroups = Array.isArray(conviventie.invitedGuestGroups)
      ? conviventie.invitedGuestGroups
      : [];

    const isGuestGroup = targetType === 'guestGroup';

    const index = isGuestGroup
      ? invitedGuestGroups.findIndex((g) => g.guestGroupId === gemeenschapId)
      : uitgenodigdeGemeenschappen.findIndex((g) => g.gemeenschapId === gemeenschapId);

    if (index === -1) {
      throw createAppError(
        APP_ERROR_CODES.TARGET_MISSING,
        isGuestGroup
          ? 'Gastengroep niet gevonden in uitgenodigde lijst'
          : 'Gemeenschap niet gevonden in uitgenodigde lijst'
      );
    }

    const cleanedLeden = (bevestigdeLeden || []).map(cleanConfirmedLid);

    const confirmedLedenCount = cleanedLeden.reduce((sum, lid) => sum + (Number(lid.aantal) || 0), 0);

    if (isGuestGroup) {
      const updatedGuestGroups = invitedGuestGroups.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
          ...item,
          uploadStatus: 'confirmed',
          uploadedAt: new Date(),
          uploadedBy: userEmail,
          bevestigdeLeden: cleanedLeden,
          updatedAt: new Date(),
          updatedBy: userEmail
        };
      });

      await updateDoc(convivRef, {
        invitedGuestGroups: updatedGuestGroups
      });
    } else {
      const updatedUitgenodigdeGemeenschappen = uitgenodigdeGemeenschappen.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
          ...item,
          uploadStatus: 'confirmed',
          uploadedAt: new Date(),
          uploadedBy: userEmail,
          bevestigdeLeden: cleanedLeden,
          updatedAt: new Date(),
          updatedBy: userEmail
        };
      });

      await updateDoc(convivRef, {
        uitgenodigdeGemeenschappen: updatedUitgenodigdeGemeenschappen
      });
    }

    await sendConviventieNotification({
      conviventieNaam: conviventie.naam,
      bisdom: isGuestGroup ? '' : uitgenodigdeGemeenschappen[index]?.bisdom,
      parochie: isGuestGroup ? '' : uitgenodigdeGemeenschappen[index]?.parochie,
      gemeenschapNaam: isGuestGroup
        ? `Gastengroep: ${invitedGuestGroups[index]?.guestGroupName || 'Onbekend'}`
        : uitgenodigdeGemeenschappen[index]?.gemeenschapNaam,
      confirmedLedenCount,
      confirmedBy: userEmail
    });
  } catch (error) {
    console.error('Error confirming leden:', error);
    throw error;
  }
};

export const buildConfirmedLedenExportRows = (conviventie) => {
  const uitgenodigdeGemeenschappen = Array.isArray(conviventie?.uitgenodigdeGemeenschappen)
    ? conviventie.uitgenodigdeGemeenschappen
    : [];
  const invitedGuestGroups = Array.isArray(conviventie?.invitedGuestGroups)
    ? conviventie.invitedGuestGroups
    : [];

  const rows = [];

  uitgenodigdeGemeenschappen.forEach((gemeenschap) => {
    const leden = Array.isArray(gemeenschap?.bevestigdeLeden)
      ? gemeenschap.bevestigdeLeden
      : [];

    leden.forEach((lid) => {
      rows.push(mapConfirmedLidToExportRow({ gemeenschap, lid }));
    });
  });

  invitedGuestGroups.forEach((guestGroup) => {
    const leden = Array.isArray(guestGroup?.bevestigdeLeden)
      ? guestGroup.bevestigdeLeden
      : [];

    leden.forEach((lid) => {
      rows.push(mapConfirmedLidToExportRow({
        gemeenschap: null,
        lid,
        guestGroupName: `Gastengroep: ${guestGroup.guestGroupName || ''}`
      }));
    });
  });

  return rows;
};
